import React, { useRef, useState } from 'react';
import { restricoesResumo } from '../lib/restricoes';
import { LEAD_STATUS_FLOW, LEAD_STATUS_CONFIG, LEAD_SECTOR_CONFIG, leadSector, ACTIVE_LEAD_STATUSES } from '../lib/leadStatus';
import { Phone, PhoneCall, Clock, CalendarClock, AlertTriangle, PhoneMissed, StickyNote, XCircle, CheckCircle2, Zap, Wifi, ShieldAlert, Tag, Calendar, Hourglass } from 'lucide-react';

const fmtShort = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('pt-PT', { day: '2-digit', month: 'short' });
  } catch { return null; }
};
const isOverdue = (iso) => { try { return iso && new Date(iso) < new Date(); } catch { return false; } };

const daysSince = (iso) => {
  if (!iso) return 0;
  try { return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000); } catch { return 0; }
};

const fmtRelative = (iso) => {
  if (!iso) return null;
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const days = Math.floor(diff / 86400000);
    if (days <= 0) return 'hoje';
    if (days === 1) return 'ontem';
    if (days < 30) return `há ${days}d`;
    const months = Math.floor(days / 30);
    return `há ${months}m`;
  } catch { return null; }
};

// Resultados sem sucesso (cliente não falou) → amarelo/vermelho de aviso
const NO_ANSWER_OUTCOMES = ['nao_atende', 'ocupado', 'voicemail'];
const outcomeStyle = (outcome) => {
  if (outcome === 'atendeu') return 'bg-emerald-100 text-emerald-700';
  if (NO_ANSWER_OUTCOMES.includes(outcome)) return 'bg-rose-100 text-rose-700';
  if (outcome === 'reagendar' || outcome === 'visita_marcada') return 'bg-blue-100 text-blue-700';
  return 'bg-slate-100 text-slate-600';
};

// Ícone por resultado do último contacto (estado mostrado quando não há nota escrita)
const OUTCOME_ICON = {
  atendeu: PhoneCall,
  nao_atende: PhoneMissed,
  ocupado: PhoneMissed,
  voicemail: PhoneMissed,
  reagendar: CalendarClock,
  visita_marcada: CalendarClock,
  sem_interesse: XCircle,
  convertido: CheckCircle2,
};

const serviceBadge = (t) => {
  if (t === 'dual') return <span className="text-[9px] px-1 py-0.5 rounded bg-indigo-100 text-indigo-700 font-semibold">L+G</span>;
  if (t === 'gas') return <span className="text-[9px] px-1 py-0.5 rounded bg-orange-100 text-orange-700 font-semibold">Gás</span>;
  if (t === 'luz') return <span className="text-[9px] px-1 py-0.5 rounded bg-amber-100 text-amber-700 font-semibold">Luz</span>;
  return null;
};

const SECTOR_ICON = { energia: Zap, telecom: Wifi, alarmes: ShieldAlert, outro: Tag };
const sectorBadge = (serviceInterest) => {
  const key = leadSector(serviceInterest);
  if (!key) return null;
  const cfg = LEAD_SECTOR_CONFIG[key];
  const Icon = SECTOR_ICON[key] || Tag;
  return (
    <span className={`text-[9px] px-1.5 py-0.5 rounded font-semibold flex items-center gap-0.5 ${cfg.badge}`} title={`Setor: ${cfg.label}`}>
      <Icon className="w-2.5 h-2.5" />{cfg.label}
    </span>
  );
};

export const LeadKanban = ({ leads, onStatusChange, onConvert, onOpen, onCall, showOwner, showClosed }) => {
  const [draggingId, setDraggingId] = useState(null);
  const [overCol, setOverCol] = useState(null);
  const boardRef = useRef(null);

  const grouped = LEAD_STATUS_FLOW.reduce((acc, s) => { acc[s] = []; return acc; }, {});
  leads.forEach((l) => { (grouped[l.status] || (grouped[l.status] = [])).push(l); });

  const visibleColumns = showClosed ? LEAD_STATUS_FLOW : ACTIVE_LEAD_STATUSES;

  const handleDrop = (e, status) => {
    const id = (e?.dataTransfer?.getData && e.dataTransfer.getData('text/plain')) || draggingId;
    setDraggingId(null);
    setOverCol(null);
    if (!id) return;
    moveTo(leads.find((l) => l.id === id), status);
  };

  const moveTo = (lead, status) => {
    if (!lead || lead.status === status) return;
    if (status === 'convertida') {
      onConvert(lead);
      return;
    }
    onStatusChange(lead, status);
  };

  return (
    <div
      ref={boardRef}
      className="flex gap-3 overflow-x-auto pb-4"
      data-testid="lead-kanban"
      onDragOver={(e) => {
        const el = boardRef.current;
        if (!el || !draggingId) return;
        const r = el.getBoundingClientRect();
        if (e.clientX > r.right - 90) el.scrollLeft += 25;
        else if (e.clientX < r.left + 90) el.scrollLeft -= 25;
      }}
    >
      {visibleColumns.map((status) => {
        const cfg = LEAD_STATUS_CONFIG[status];
        const items = grouped[status] || [];
        const isConvertCol = status === 'convertida';
        return (
          <div
            key={status}
            className={`flex-shrink-0 w-[260px] rounded-xl bg-slate-50 border-t-4 ${cfg.column} ${overCol === status ? 'ring-2 ring-[#2D3E6F]/40 bg-slate-100' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setOverCol(status); }}
            onDragLeave={() => setOverCol((c) => (c === status ? null : c))}
            onDrop={(e) => handleDrop(e, status)}
            data-testid={`kanban-col-${status}`}
          >
            <div className="flex items-center justify-between px-3 py-2 sticky top-0">
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${cfg.color}`}>{cfg.label}</span>
              <span className="text-xs font-semibold text-slate-500">{items.length}</span>
            </div>
            <div className="px-2 pb-2 space-y-2 min-h-[120px] max-h-[calc(100vh-340px)] overflow-y-auto">
              {items.map((lead) => {
                const overdue = isOverdue(lead.next_action_at) && !['convertida', 'sem_interesse', 'invalida', 'incontactavel'].includes(lead.status);
                const attempts = lead.contact_attempts || 0;
                const noAnswerStreak = NO_ANSWER_OUTCOMES.includes(lead.last_contact_outcome);
                const isDifficult = attempts >= 3 && noAnswerStreak;
                const noteText = (lead.last_contact_note || '').trim();
                const OutcomeIcon = OUTCOME_ICON[lead.last_contact_outcome] || Phone;
                return (
                  <div
                    key={lead.id}
                    draggable
                    onDragStart={(e) => { e.dataTransfer.setData('text/plain', lead.id); e.dataTransfer.effectAllowed = 'move'; setDraggingId(lead.id); }}
                    onDragEnd={() => { setDraggingId(null); setOverCol(null); }}
                    onClick={() => onOpen(lead)}
                    className={`bg-white rounded-lg border p-2.5 cursor-pointer hover:shadow-md transition-shadow ${draggingId === lead.id ? 'opacity-40' : ''} ${isDifficult ? 'border-rose-300 border-l-4 border-l-rose-500' : overdue ? 'border-slate-200 border-l-4 border-l-rose-400' : 'border-slate-200'}`}
                    data-testid={`kanban-card-${lead.id}`}
                  >
                    <div className="flex items-start justify-between gap-1">
                      <p className="text-sm font-semibold text-slate-800 truncate">{lead.client_name}</p>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {sectorBadge(lead.service_interest)}
                        {serviceBadge(lead.service_type)}
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-1 mt-0.5">
                      <p className="text-xs text-slate-500 font-mono flex items-center gap-1"><Phone className="w-3 h-3" />{lead.client_phone || '—'}</p>
                      {lead.client_phone && (
                        <a href={`tel:${String(lead.client_phone).replace(/\s/g, '')}`} draggable={false}
                          onClick={(e) => { e.stopPropagation(); setTimeout(() => onCall?.(lead), 400); }}
                          className="flex items-center gap-1 text-[11px] font-semibold bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-2.5 py-1 transition-colors"
                          data-testid={`kanban-call-${lead.id}`}><Phone className="w-3 h-3" />Ligar</a>
                      )}
                    </div>
                    {lead.restricoes?.length > 0 && (
                      <p className="mt-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-600 text-white w-fit max-w-full truncate" title={restricoesResumo(lead.restricoes)} data-testid={`kanban-restricao-${lead.id}`}>
                        {restricoesResumo(lead.restricoes)}
                      </p>
                    )}
                    {showOwner && lead.assigned_to_name && (
                      <p className="text-[11px] text-slate-400 truncate mt-0.5">{lead.assigned_to_name}</p>
                    )}
                    {lead.created_at && (
                      <p className="text-[10px] text-slate-400 flex items-center gap-1 mt-0.5" title={`Carregada em ${new Date(lead.created_at).toLocaleString('pt-PT')}`} data-testid={`kanban-loaded-${lead.id}`}>
                        <Calendar className="w-2.5 h-2.5" /> Carregada {fmtShort(lead.created_at)} ({fmtRelative(lead.created_at)})
                      </p>
                    )}
                    {attempts === 0 && lead.status === 'nova' && daysSince(lead.created_at) >= 2 && (
                      <div className={`mt-1 text-[10px] font-bold px-1.5 py-0.5 rounded flex items-center gap-1 w-fit ${daysSince(lead.created_at) >= 5 ? 'bg-red-600 text-white' : 'bg-orange-100 text-orange-700'}`} data-testid={`kanban-stalled-${lead.id}`}>
                        <Hourglass className="w-2.5 h-2.5" /> Sem ação há {daysSince(lead.created_at)}d
                      </div>
                    )}
                    {isDifficult && (
                      <div className="mt-1.5 text-[10px] font-bold px-1.5 py-1 rounded bg-rose-600 text-white flex items-center gap-1" data-testid={`kanban-difficult-${lead.id}`}>
                        <AlertTriangle className="w-3 h-3" /> LEAD DIFÍCIL · {attempts}× sem sucesso
                      </div>
                    )}
                    {attempts > 0 && (
                      <div className="flex items-center flex-wrap gap-1 mt-1.5" data-testid={`kanban-attempts-${lead.id}`}>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold flex items-center gap-0.5 ${noAnswerStreak ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-600'}`} title={`${attempts} tentativa(s) de contacto`}>
                          {noAnswerStreak ? <PhoneMissed className="w-2.5 h-2.5" /> : <Phone className="w-2.5 h-2.5" />}
                          {attempts}×
                        </span>
                        {lead.last_contact_at && (
                          <span className="text-[10px] text-slate-400">{fmtRelative(lead.last_contact_at)}</span>
                        )}
                      </div>
                    )}
                    {noteText ? (
                      <p className="text-[11px] text-amber-700 bg-amber-50 rounded px-1.5 py-1 mt-1.5 flex items-start gap-1 line-clamp-2 break-words" title={noteText} data-testid={`kanban-note-${lead.id}`}>
                        <StickyNote className="w-3 h-3 flex-shrink-0 mt-0.5" />
                        <span className="line-clamp-2">{noteText}</span>
                      </p>
                    ) : lead.last_contact_outcome_label ? (
                      <div className={`text-[11px] font-semibold rounded px-1.5 py-1 mt-1.5 flex items-center gap-1 ${outcomeStyle(lead.last_contact_outcome)}`} title={`Último resultado: ${lead.last_contact_outcome_label}`} data-testid={`kanban-state-${lead.id}`}>
                        <OutcomeIcon className="w-3 h-3 flex-shrink-0" />
                        {lead.last_contact_outcome_label}
                      </div>
                    ) : null}
                    {lead.next_action_at && (
                      <p className={`text-[11px] mt-1 flex items-center gap-1 ${overdue ? 'text-rose-600 font-semibold' : 'text-slate-400'}`}>
                        {overdue ? <AlertTriangle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                        {fmtShort(lead.next_action_at)}
                      </p>
                    )}
                    <select
                      value=""
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => { e.stopPropagation(); moveTo(lead, e.target.value); }}
                      className="mt-2 w-full text-[11px] border border-slate-200 rounded-md px-1.5 py-1 bg-slate-50 text-slate-600 cursor-pointer hover:border-[#2D3E6F]"
                      data-testid={`kanban-move-${lead.id}`}
                    >
                      <option value="" disabled>Mover para…</option>
                      {LEAD_STATUS_FLOW.filter((st) => st !== lead.status).map((st) => (
                        <option key={st} value={st}>{LEAD_STATUS_CONFIG[st]?.label || st}</option>
                      ))}
                    </select>
                  </div>
                );
              })}
              {items.length === 0 && (
                <div className="text-center text-[11px] text-slate-300 py-6">
                  {isConvertCol ? 'Arrasta para cá para converter' : 'Vazio'}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default LeadKanban;
